import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "./Reveal";
import { OrganicDot } from "./Logo";

export const Amp = ({ text }) => text.split("&").flatMap((p, i) => (i ? [<span key={i} className="font-sans font-bold not-italic text-[0.8em]">&amp;</span>, p] : [p]));

export function LogoTitle({ title, script, center = false, size = "lg", light = false, as: Tag = "h1" }) {
  const big = size === "lg";
  return (
    <Tag className={`leading-none flex flex-col ${center ? "items-center" : "items-start"}`}>
      <span className={`font-logo font-black tracking-[-0.025em] ${light ? "text-white" : "text-[#13261c]"} ${big ? "text-5xl sm:text-6xl lg:text-[80px]" : "text-4xl lg:text-[52px]"}`} style={{ lineHeight: 0.95 }}>
        {title}<OrganicDot className={`inline-block align-baseline ml-1 ${big ? "w-3 h-3 lg:w-5 lg:h-5" : "w-2.5 h-2.5 lg:w-3.5 lg:h-3.5"}`} />
      </span>
      {script && <span className={`block font-script text-[#d9895a] ${big ? "text-4xl sm:text-5xl lg:text-[60px] mt-1 lg:mt-2" : "text-3xl lg:text-[40px] mt-2"} ${center ? "text-center" : "ml-1"}`}>{script}</span>}
    </Tag>
  );
}

export function SectionTitle({ kicker, title, script, text, center = false, light = false }) {
  return (
    <Reveal className={center ? "text-center max-w-3xl mx-auto" : "max-w-2xl"}>
      <span className={`text-sm font-bold uppercase tracking-[0.2em] ${light ? "text-[#d9895a]" : "text-[#c4703f]"}`}>{kicker}</span>
      {script ? <div className="mt-4"><LogoTitle as="h2" size="md" title={title} script={script} center={center} light={light} /></div>
        : <h2 className={`font-display text-4xl lg:text-[56px] font-semibold mt-3 leading-[1.05] ${light ? "text-white" : "text-[#13261c]"}`}>{title}</h2>}
      {text && <p className={`mt-5 text-lg leading-relaxed ${light ? "text-[#faf7f0]/85" : "text-[#5a6158]"}`}>{text}</p>}
    </Reveal>
  );
}

export function CtaLink({ to, children, variant = "primary", testid }) {
  const cls = variant === "primary"
    ? "bg-[#13261c] hover:bg-[#2a4636] text-white"
    : variant === "yellow" ? "bg-[#d9895a] hover:bg-[#e39d72] text-[#13261c]" : "bg-white hover:bg-[#f1ebdd] text-[#13261c] ring-1 ring-[#dccfb6]";
  return (
    <Link to={to} data-testid={testid} className={`group inline-flex items-center gap-2 rounded-full px-7 py-4 font-bold transition-colors ${cls}`}>
      {children} <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
    </Link>
  );
}

export function PageHero({ kicker, title, script, text, children }) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#f3ede1] to-[#faf7f0]">
      <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#d9895a]/15 blur-3xl" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 lg:pt-24 pb-14 relative text-center">
        <Reveal><span className="text-sm font-bold uppercase tracking-[0.2em] text-[#c4703f]">{kicker}</span></Reveal>
        <Reveal delay={80}>{script ? <div className="mt-5"><LogoTitle center title={title} script={script} /></div> : <h1 className="font-display mt-4 text-5xl lg:text-[68px] font-semibold text-[#13261c] leading-[1.03] max-w-4xl mx-auto">{title}</h1>}</Reveal>
        {text && <Reveal delay={160}><p className="mt-6 text-lg lg:text-xl text-[#4a5249] max-w-2xl mx-auto leading-relaxed">{text}</p></Reveal>}
        {children}
      </div>
    </section>
  );
}
